import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isRealNativePlatform } from "./platform";
import {
  takeLaunchQuickActionRoute,
  takeLiveQuickActionRoute,
} from "./quickActions";

/**
 * Routes native quick actions into the SPA.
 *
 * The Home Screen quick action ("New Voice Note") and the matching App
 * Shortcut (Action Button / Siri) both arrive as a synthetic
 * `<bundle-id>://quick-action/…` deep link (see QuickActions.swift). A cold
 * launch surfaces it via `App.getLaunchUrl()`; a warm one fires `appUrlOpen`.
 * Either way we navigate to the mapped route — the target screen owns the
 * autostart semantics (and refuses to clobber an in-progress recording).
 *
 * Renders nothing and no-ops on web.
 */
export function NativeQuickActionListener() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isRealNativePlatform()) return;

    let cancelled = false;
    let removeListener: (() => void) | undefined;

    // Wherever the user currently is: a workspace scope there (query param
    // or the /workspaces/:id path) rides along onto the voice route so a
    // workspace-scoped screen keeps its scope. Read at handle time
    // (BrowserRouter keeps window.location authoritative) rather than via
    // useLocation, which would re-subscribe on every navigation.
    const currentLocation = () => ({
      pathname: window.location.pathname,
      search: window.location.search,
    });

    const setup = async (): Promise<void> => {
      const { App } = await import("@capacitor/app");

      // The app may have been cold-launched by a quick action.
      const launch = await App.getLaunchUrl();
      const launchRoute = takeLaunchQuickActionRoute(
        launch?.url,
        currentLocation(),
      );
      if (launchRoute && !cancelled) navigate(launchRoute);

      const sub = await App.addListener("appUrlOpen", (event) => {
        const to = takeLiveQuickActionRoute(event.url, currentLocation());
        if (to && !cancelled) navigate(to);
      });
      removeListener = () => void sub.remove();
    };

    void setup();

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, [navigate]);

  return null;
}
